import React, { PropsWithChildren } from 'react';
import { SerializedStyles, css } from '@emotion/react';
import styled from '@emotion/styled';

import {
  darkComponent,
  oneZIndex,
  sizeTabletLg,
  turquoise,
  sizeMobile,
  black80,
  typography,
  breakpoints,
  fontWeight,
} from '~/styles';

export const styles = {
  header: css(typography.mobileHeading4, {
    color: 'var(--Grey-Text)',
    textTransform: 'capitalize',
    [breakpoints.minTablet]: [typography.tabletSubheading2Bold],
    [breakpoints.minDesktop]: [typography.desktopSubheading1, { fontWeight: fontWeight.bold }],
  }),
  seeAllBtn: css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    svg: {
      height: '12px',
    },
  }),
  seeAllBtnContainer: css({
    display: 'flex',
    alignItems: 'center',
    marginRight: 'var(--Horizontal-Padding, 0px)',
    '&:hover': {
      textDecoration: 'underline',
    },
  }),
};

export const Header = styled.h4`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--Section-Header-padding, 16px);
  ${/* //necessary for current z-index hack, slider overflow issue */ undefined};
  z-index: ${oneZIndex};
  position: relative;
`;

export const HeadingText = styled.span`
  color: ${turquoise};
  margin-bottom: 14px;
  font-size: 14px;
  letter-spacing: 3px;
  font-weight: 600;
  text-align: left;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  ${sizeMobile(css`
    font-size: 12px;
  `)}
`;

interface SectionProps {
  dark?: boolean;
  tall?: boolean;
  className?: string;
}

export const Section: React.FC<PropsWithChildren<SectionProps>> = styled.section<SectionProps>`
  ${({ dark }: SectionProps) =>
    dark &&
    css`
      ${darkComponent};
      color: white;
      position: relative;
      z-index: ${oneZIndex};
    `};
`;

interface WrapperProps {
  slickTrackHeight?: string;
  hasHoverableCards?: boolean;
  paddingTop?: string;
  paddingBottom?: string;
  lineWidthPercentage?: number;
  styleDotsAsLines?: boolean;
  overrideSlickCarouselCSS?: SerializedStyles | {};
}

/* code for Wrapper below, as we are not using this anywhere so disabling it
  ({ hasHoverableCards, isMeditationCarousel }: WrapperProps) =>
    hasHoverableCards &&
    css`
      .slick-current {
        .MuiCard-root {
          position: relative;
          left: 0;
          :hover {
            left: ${isMeditationCarousel ? '14px' : '24px'};
          }
        }
      }
    `*/

export const Wrapper = styled.div<WrapperProps>`
  .slick-slide {
    padding: 0 8px;
  }

  .slick-slide:first-of-type {
    padding-left: 0px;
  }
  .slick-slide > div:first-of-type {
    margin-bottom: 11px;
  }
  .slick-track {
    display: flex;
    margin-left: 0;
    margin-right: 0;
    height: 100%;
    ${({ slickTrackHeight }: WrapperProps) =>
      slickTrackHeight &&
      css`
        height: ${slickTrackHeight};
      `};
  }

  ul.slick-dots {
    position: absolute;
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: center;
    bottom: -30px;
  }

  .slick-dots li {
    position: relative;
    display: inline-block;
    width: 11.5px;
    height: 11.5px;
    margin: 0 5px;
    padding: 0;
    cursor: pointer;
  }

  .slick-dots li button {
    font-size: 0;
    line-height: 0;
    display: block;
    width: 8px;
    height: 8px;
    padding: 5px;
    cursor: pointer;
    color: transparent;
    border: 0;
    outline: 0;
    background: #d8d8d8;
    border: none;
    border-radius: 50%;

    &:hover {
      background: #ff642e;
    }
  }

  li.slick-active button,
  li.slick-active button:hover {
    background-color: #ff642e;
    cursor: default;
  }

  .slick-list {
    min-height: 420px;
    margin-left: -10px;
    z-index: 2;

    @media (max-width: 1023px) {
      margin: 0;
    }
  }

  .slick-slide {
    padding: 0 15px;
  }

  .slick-arrow {
    border-radius: 50%;
    width: 56px;
    height: 56px;
    background: #fff;
    color: rgba(0, 0, 0, 0.54);
    border: none;
    cursor: pointer;
    top: 50%;
    position: absolute;
    top: calc(50% - 25px);
    z-index: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    svg {
      stroke: rgba(0, 0, 0, 0.54);
      width: 9.4px;
      height: 13px;
      transition: 0.4s all ease;
      path {
        transform: scale(0.63, 0.58);
      }
    }
    &:hover {
      -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.2);
      -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.2);
      box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.2);
      background-color: #fff;
      transition: 0.4s all ease;
      svg {
        transform: scale(1.4);
      }
    }
    &:focus {
      background-color: #fff;
    }
  }

  .slick-slider {
    max-height: 100%;
  }

  [data-slick] {
    display: none;

    &.slick-initialized {
      display: block;
    }
  }

  .slick-prev {
    left: -30px;
    &::before {
      content: '';
    }
    @media screen and (max-width: 767px) {
      left: 0;
    }
  }

  .slick-next {
    right: -20px;
    &::before {
      content: '';
    }
    @media screen and (max-width: 767px) {
      right: 0;
    }
  }

  .slick-arrow {
    @media screen and (max-width: 767px) {
      width: 46px;
      height: 46px;
    }
  }

  .slick-dots li button:before {
    font-family: 'slick';
    font-size: 0;
    line-height: 0px;
    width: 0;
    height: 0;
  }
  ${({ styleDotsAsLines, lineWidthPercentage }: WrapperProps) =>
    styleDotsAsLines &&
    css`
      ul.slick-dots {
        display: flex !important;
        position: absolute;
        z-index: ${oneZIndex};
        top: 16px;
        height: 2px;
        width: ${lineWidthPercentage}%;
        padding: 0;
        margin: 0;
        list-style: none;
        background: rgba(45, 179, 198, 0.24);
      }

      .slick-dots li {
        position: relative;
        flex: 1;
        height: 2px;
        margin: 0;
        padding: 0;
        cursor: pointer;
      }

      li.slick-active {
        background: rgba(45, 179, 198, 1);
        opacity: 1;
      }

      .slick-dots li button {
        display: none;
      }
      .slick-list {
        min-height: 100%;
        margin-left: -8px;
        overflow-x: clip;
        overflow-y: unset;

        ${sizeTabletLg(css`
          margin: 0;
        `)};
      }
    `};

  ${({ overrideSlickCarouselCSS }: WrapperProps) => overrideSlickCarouselCSS}
`;

const LargeHeaderWrapper = styled.div`
  display: block;
  margin: auto;
`;

const LargeHeaderTitle = styled.div`
  text-align: center;
  font-size: 30px;
  color: ${black80};
  font-weight: 400;
  letter-spacing: 2.5px;
  ${sizeMobile(css`
    font-size: 20px;
  `)}
`;

const LargeHeaderSubTitle = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  color: ${black80};
  letter-spacing: 0.5px;
  word-spacing: 0.5px;
  max-width: 650px;
  margin: auto;
  margin-top: 10px;
  text-align: center;
  ${sizeMobile(css`
    font-size: 14px;
  `)}
`;

interface LargeHeaderProps {
  title: string;
  subtitle?: string;
}
export const LargeCarouselHeader: React.FC<PropsWithChildren<LargeHeaderProps>> = ({
  title,
  subtitle = '',
}) => (
  <LargeHeaderWrapper>
    <LargeHeaderTitle>{title}</LargeHeaderTitle>
    <LargeHeaderSubTitle>{subtitle}</LargeHeaderSubTitle>
  </LargeHeaderWrapper>
);
